// Screen 6 · Derived-plan review — the fixed 5-stage plan rendered, with the three
// cells in their slots. Empty cells read as friendly prompts, never "MISSING".
// Owner can Submit if still a draft. (Lead can view content — teacher+Lead tier.)

import { api } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";

interface RenderedCell {
  label: string;
  value: string | null;
  emptyPrompt: string;
}
interface RenderedStage {
  key: string;
  title: string;
  cells?: RenderedCell[];
}
interface PlanResponse {
  plan_id: string;
  lesson: { id: string; topic: string; class_name: string; week_no: number; status: string };
  rendered: {
    priorKnowledge: RenderedCell;
    stages: RenderedStage[];
    materials: RenderedCell;
    procedure: RenderedCell;
  };
  timing: { started_at: string | null; completed_at: string | null; duration_sec: number | null };
  can_submit: boolean;
  can_approve: boolean;   // Lead, status submitted
  can_reopen: boolean;    // Lead, status approved
  locked: boolean;        // status approved — no edits until re-opened
  content_changed: boolean; // edited after submitting (drift vs the snapshot)
}

function cellBlock(cell: RenderedCell): string {
  const content = cell.value
    ? `<p>${esc(cell.value)}</p>`
    : `<p class="hint">${esc(cell.emptyPrompt)}</p>`;
  return `<div style="margin-top:8px"><strong>${esc(cell.label)}</strong>${content}</div>`;
}

// A plain, un-ranked statement of effort — never a score or a comparison (the rails).
function timingLine(durationSec: number | null): string {
  if (durationSec === null) return "";
  const mins = Math.round(durationSec / 60);
  const text = mins < 1 ? "Planned in under a minute." : `Planned in about ${mins} min.`;
  return `<p class="muted">${esc(text)}</p>`;
}

export async function renderPlanView(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const data = await api<PlanResponse>(`/api/plans/${encodeURIComponent(params.id!)}`);
  const { lesson, rendered } = data;

  const stages = rendered.stages
    .map(
      (s) =>
        `<div class="card"><h2 style="margin-top:0">${esc(s.title)}</h2>${(s.cells ?? [])
          .map(cellBlock)
          .join("")}</div>`,
    )
    .join("");

  app.innerHTML = `
    <div class="topbar"><a data-link href="/this-week" class="muted">← This Week</a></div>
    <h1>Here's your plan.</h1>
    <p class="muted">${esc(lesson.class_name)} · ${esc(lesson.topic)} · Week ${esc(lesson.week_no)}</p>
    ${timingLine(data.timing.duration_sec)}

    <div class="card">${cellBlock(rendered.priorKnowledge)}</div>
    ${stages}
    <div class="card">${cellBlock(rendered.materials)}</div>
    <div class="card">${cellBlock(rendered.procedure)}</div>

    ${driftNote(data.content_changed)}
    <div id="err" class="error" hidden></div>
    <div style="height:8px"></div>
    ${actionArea(data)}`;

  // Actions are gated server-side (the flags above); the click handlers just call the route and
  // refresh. Submit returns the teacher to This Week; approve/re-open re-render the plan in place.
  const err = app.querySelector<HTMLElement>("#err")!;
  const act = async (path: string, after: () => void | Promise<void>): Promise<void> => {
    err.hidden = true;
    try {
      await api(path, { method: "POST" });
      await after();
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  };
  app.querySelector("#submit")?.addEventListener("click", () => act(`/api/lessons/${lesson.id}/submit`, () => navigate("/this-week")));
  app.querySelector("#approve")?.addEventListener("click", () => act(`/api/lessons/${lesson.id}/approve`, () => renderPlanView(app, params)));
  app.querySelector("#reopen")?.addEventListener("click", () => act(`/api/lessons/${lesson.id}/reopen`, () => renderPlanView(app, params)));
}

// A quiet, non-alarming notice — never red. Tells owner + Lead the plan changed after submitting.
function driftNote(changed: boolean): string {
  return changed ? `<p class="muted">Edited after submitting — re-submit to update.</p>` : "";
}

// The single action affordance for the current role + status. Neutral copy; Approved reads calm.
function actionArea(d: PlanResponse): string {
  if (d.can_submit) return `<button class="btn" id="submit" type="button">Submit</button>`;
  if (d.can_approve) return `<p class="muted">Submitted ✓</p><button class="btn" id="approve" type="button">Approve</button>`;
  if (d.can_reopen) return `<p class="muted">Approved ✓ · locked</p><button class="btn secondary" id="reopen" type="button">Re-open to edit</button>`;
  if (d.locked) return `<p class="muted">Approved ✓ · locked</p>`;
  if (d.lesson.status === "submitted") return `<p class="muted">Submitted ✓</p>`;
  return "";
}
